import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, BarChart, Zap, LifeBuoy } from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';

const efficiencyServices = [
  {
    icon: TrendingUp,
    title: 'Performance Optimization',
    description: 'Tuning your systems for maximum performance and efficiency to enhance productivity.',
  },
  {
    icon: BarChart,
    title: 'Resource Management',
    description: 'Effective allocation and utilization of IT resources to minimize waste and reduce costs.',
  },
  {
    icon: Zap,
    title: 'Process Automation',
    description: 'Automating routine IT tasks to improve efficiency and reduce human error.',
  },
  {
    icon: LifeBuoy,
    title: 'Technical Consultations',
    description: 'Expert advice on technology decisions to ensure alignment with business objectives.',
  },
];

const Efficiency = () => {
  return (
    <section className="py-20 bg-white">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading
              title="Efficiency Building & Technical Consultations"
              subtitle="Optimizing your IT infrastructure for maximum performance."
            />
            
            <p className="text-gray-600 mb-8">
              Beyond maintenance and support, we offer specialized services aimed at enhancing the efficiency 
              of your IT operations. Our team of experts analyzes your current setup, identifies areas for 
              improvement, and implements changes that lead to measurable performance gains and cost savings.
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Benefits of Our Efficiency Services</h3>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="text-green-500 mr-2">•</div>
                  <div className="text-gray-700">
                    <span className="font-semibold">Reduced Operational Costs:</span> Optimized systems require less maintenance and consume fewer resources.
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="text-green-500 mr-2">•</div>
                  <div className="text-gray-700">
                    <span className="font-semibold">Improved Productivity:</span> Faster systems and streamlined processes enhance user productivity.
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="text-green-500 mr-2">•</div>
                  <div className="text-gray-700">
                    <span className="font-semibold">Enhanced Reliability:</span> Optimized infrastructure experiences fewer failures and offers better performance.
                  </div>
                </li>
                
                <li className="flex items-start">
                  <div className="text-green-500 mr-2">•</div>
                  <div className="text-gray-700">
                    <span className="font-semibold">Better Resource Utilization:</span> Proper allocation ensures you get the most from your IT investments.
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
          
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {efficiencyServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white p-6 rounded-lg shadow-md border border-gray-100"
                  >
                    <div className="text-blue-600 mb-4">
                      <Icon size={32} />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </motion.div>
                );
              })}
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-8 bg-blue-50 p-6 rounded-lg border border-blue-100"
            >
              <h3 className="text-lg font-semibold mb-3 text-blue-800">Our Approach</h3>
              <p className="text-gray-700">
                We take a systematic approach to efficiency building, starting with a thorough assessment 
                of your current IT environment, followed by the development of a detailed improvement plan, 
                and culminating in the implementation of targeted optimizations with measurable outcomes.
              </p>
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Efficiency;